import React from "react";

export function getCustomMessageClass(customType) {
    const messageClassMap = {
        "sample_request": "sample-request-message",
        "information_request": "information-request-message",
        "pricing_request": "pricing-request-message",
        "ai_assistant": "ai-assistant-message",
    };
    return messageClassMap[customType] || "";
}

export function renderRequestElements(requestObject, requestOptions, customType, requestId) {
    if (!requestOptions?.value) return null;
    if (!requestObject && customType !== 'ai_assistant') return null;

    return requestOptions.value.map((option) => (
        <button
            id={`${getCustomMessageClass(customType)}-action-${requestId}-${formatAction(option)}`}
            key={option}
            className={actionClass(formatAction(option))}
            onClick={() => handleMessageActions(customType, formatAction(option), requestId)}
            disabled={
                customType !== 'ai_assistant' &&
                actionDisabled(customType, formatAction(option), requestObject)
            }
            style={{marginTop: 0.5 + 'rem', marginBottom: 0.5 + 'rem'}}
        >
            {option}
        </button>
    ));
}

export function formatAction(action) {
    return action.replace(/\s+/g, "-").toLowerCase();
}

const typeToBaseUrlMap = {
    "sample_request": "/deal_sample_requests",
    "information_request": "/deal_information_requests",
    "pricing_request": "/deal_pricing_requests",
    "ai_assistant": "/conversations",
};

export function handleMessageActions(customType, action, requestId) {
    const baseUrl = typeToBaseUrlMap[customType];
    if (!baseUrl) return;

    processRequest(baseUrl, action, requestId);
}

function processRequest(baseUrl, action, requestId) {
    const actionHandlers = {
        "accept": () => viewRequest(baseUrl, requestId),
        "reject": () => viewRequest(baseUrl, requestId),
        "view": () => viewRequest(baseUrl, requestId),
        "contact-us": () => (window.location.href = "/conversations/support"),
        "contact-kwayga-support": () => (window.location.href = "/conversations/support"),
        "add-tracking-information": () => viewRequest(baseUrl, requestId),
        "add-information": () => viewRequest(baseUrl, requestId),
        "create-draft": () => sendMessage(baseUrl, requestId, "Create Draft"),
        "start-again": () => sendMessage(baseUrl, requestId, "Start Again"),
        "edit": () => sendMessage(baseUrl, requestId, "Edit"),
    };

    actionHandlers[action]?.();
}

export function actionClass(action) {
    const actionClassMap = {
        accept: "primary-button",
        reject: "error-button",
        "contact-us": "secondary-button",
        "contact-kwayga-support": "secondary-button",
        "add-tracking-information": "secondary-button",
        "add-information": "secondary-button",
        "create-draft": "tertiary-button",
        "start-again": "error-button",
        "edit": "secondary-button",
    };

    return actionClassMap[action] || "tertiary-button";
}

export function requestHeader(customType) {
    switch (customType) {
        case "sample_request":
            return "Sample Request";
        case "information_request":
            return "Information Request";
        case "pricing_request":
            return "Pricing Request";
        default:
            return "";
    }
}

const COMMON_STATUS_LABELS = Object.freeze({
    requested: "Awaiting response",
    rejected: "Rejected",
    completed: "Completed",
});

const STATUS_LABEL_MAPS = Object.freeze({
    sample_request: {
        ...COMMON_STATUS_LABELS,
        accepted: "Awaiting tracking information",
        samples_shipped: "Awaiting samples",
        samples_delivered: "Samples received",
    },
    information_request: {
        ...COMMON_STATUS_LABELS,
        accepted: "Awaiting information",
        information_provided: "Awaiting review",
    },
    pricing_request: {
        ...COMMON_STATUS_LABELS,
        accepted: "Awaiting pricing information",
        pricing_provided: "Awaiting review",
    },
});

export function mapRequestStatus(status, customType) {
    const typeMap = STATUS_LABEL_MAPS[customType];
    if (!typeMap) return status;
    return typeMap[status] || status;
}

export function actionDisabled(customType, action, requestObject) {
    if (customType === "ai_assistant") {
        return false
    }

    const actionDisabledMap = {
        accept: requestObject.status !== "requested",
        reject: requestObject.status !== "requested",
        "contact-us": false,
        "contact-kwayga-support": false,
        "add-tracking-information": ["samples_shipped", "completed"].includes(requestObject.status),
        "add-information": ["information_provided", "pricing_provided", "completed"].includes(requestObject.status),
    };

    return actionDisabledMap[action] || false;
}

export function getRequestObject(customType, requestId) {
    const baseUrl = typeToBaseUrlMap[customType];
    if (!baseUrl) {
        return Promise.resolve(null);
    }

    return $.ajax({
        url: `${baseUrl}/${requestId}`,
        method: "GET",
        dataType: "json"
    })
        .then((response) => {
            return response;
        })
        .catch((error) => {
            console.error("Error fetching request object:", error);
            return null;
        });
}

function viewRequest(baseUrl, requestId) {
    window.location.href = `${baseUrl}/${requestId}`;
}

function addAdditionalInfo(baseUrl, requestId) {
    $.ajax({
        url: `${baseUrl}/${requestId}/add_additional_information`,
        type: "GET",
    });
}

function sendMessage(baseUrl, requestId, message) {
    $.ajax({
        url: `${baseUrl}/${requestId}/send_message`,
        type: "POST",
        data: {message}
    }).done(() => {
        scrollToBottom();
    });
}

export const styles = {
    actionsContainer: isMobile => ({
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: isMobile ? 'center' : 'space-between',
        borderTop: "none",
        gap: "1rem"
    }),
    link: {
        color: "#154254",
        textDecoration: "underline",
        cursor: "pointer"
    }
};

export function renderAuthenticatedFileLinks(fileInput, conversationId) {
    const fileUrl = fileInput?.url;
    const fileName = fileInput?.name || fileUrl || 'File';
    return (
        <a
            style={styles.link}
            onClick={async (e) => {
                e.preventDefault();
                const {hostname} = new URL(fileUrl, window.location.origin);
                if (hostname.includes('sendbird')) {
                    await openAuthenticatedFileInNewTab(fileUrl, conversationId);
                } else {
                    openUrlInNewTab(fileUrl);
                }
            }}
        >
            {fileName}
        </a>
    );
}

async function openAuthenticatedFileInNewTab(fileUrl, conversationId) {
    if (!fileUrl || !conversationId) {
        window.sentry?.captureMessage?.('Missing fileUrl or conversationId when authenticating file');
        return;
    }
    try {
        const result = await $.ajax({
            url: `/conversations/${conversationId}/authenticate_file`,
            method: 'GET',
            dataType: 'json',
            data: {file_url: fileUrl}
        });
        const signedUrl = result?.file_url;
        if (!signedUrl) {
            window.sentry?.captureMessage?.('Authentication succeeded but no URL was returned');
            return;
        }
        openUrlInNewTab(signedUrl);
    } catch (err) {
        window.sentry?.captureException?.(err);
    }
}

function openUrlInNewTab(url) {
    window.open(url, '_blank', 'noopener,noreferrer');
}

export function scrollToBottom() {
    setTimeout(() => {
        const scrollContainer = document.querySelector('.sendbird-conversation__messages-padding');
        if (scrollContainer) {
            scrollContainer.scrollTo({
                top: scrollContainer.scrollHeight,
                behavior: 'smooth'
            });
        }
    }, 100);
}
